import React, { useState, useEffect } from "react";
import "./AddPassengerCountDialog.css";
import { Button, CircularProgress } from "@mui/material";
import SnackbarAlert from "../../../../../components/element/SnackbarAlert";
import { useConfiguration } from "../../../../../services/ConfigurationProvider";
import {
  getGovernmentPassenger,
  getDiplomatsPassenger,
  getMilitaryPassenger,
  getInfantPassenger,
} from "../../../../../utils/configUtils";
import AddCrewMembersDialog from "./AddCrewMembersDialog";
import { PlusOutlined } from "@ant-design/icons";

const AddPassengerCountDialog = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  isLoading,
}) => {
  // Get configuration values
  const { configurationData } = useConfiguration();

  const governmentPassengerEnabled = getGovernmentPassenger(configurationData);
  const diplomatsPassengerEnabled = getDiplomatsPassenger(configurationData);
  const militaryPassengerEnabled = getMilitaryPassenger(configurationData);
  const infantPassengerEnabled = getInfantPassenger(configurationData);

  const [passengerCounts, setPassengerCounts] = useState({
    totalBoarded: 0,
    totalExempted: 0,
    infant: 0,
    military: 0,
    government: 0,
    diplomat: 0,
  });

  const [error, setError] = useState("");
  const [errorKey, setErrorKey] = useState(0);
  const [crewMembers, setCrewMembers] = useState([]);
  const [isCrewMembersDialogOpen, setIsCrewMembersDialogOpen] = useState(false);

  // Update passenger counts when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setPassengerCounts({
        totalBoarded: initialData?.airlinePassengerCount || 0,
        totalExempted: initialData?.airlineExemptedPassengerCount || 0,
        infant: initialData?.airlineInfant || 0,
        military: initialData?.airlineMilitary || 0,
        government: initialData?.airlineGovernment || 0,
        diplomat: initialData?.airlineDiplomats || 0,
      });
      // Initialize crew members if provided
      if (initialData?.crewMembers) {
        setCrewMembers(initialData.crewMembers);
      } else if (
        initialData?.deadheadCrews &&
        initialData.deadheadCrews.length > 0
      ) {
        // Convert deadheadCrews format to crew members format
        const convertedCrewMembers = initialData.deadheadCrews.map((crew) => {
          return {
            id: crew?.id || null,
            name: crew.passengerName,
            seatNumber: crew.seatNumber,
            passengerName: crew.passengerName,
          };
        });

        setCrewMembers(convertedCrewMembers);
      } else {
        setCrewMembers([]);
      }
    }
  }, [initialData]);

  // Calculate total exempted passengers whenever the four categories or crew members change
  useEffect(() => {
    let totalExempted = 0;
    if (infantPassengerEnabled) {
      totalExempted += getNumericValue(passengerCounts.infant);
    }

    // Only include military if enabled
    if (militaryPassengerEnabled) {
      totalExempted += getNumericValue(passengerCounts.military);
    }
    // Only include government if enabled
    if (governmentPassengerEnabled) {
      totalExempted += getNumericValue(passengerCounts.government);
    }
    // Only include diplomat if enabled
    if (diplomatsPassengerEnabled) {
      totalExempted += getNumericValue(passengerCounts.diplomat);
    }
    // Include crew members count in exempted passengers
    totalExempted += crewMembers.length;

    setPassengerCounts((prev) => ({
      ...prev,
      totalExempted,
    }));
  }, [
    passengerCounts.infant,
    passengerCounts.military,
    passengerCounts.government,
    passengerCounts.diplomat,
    passengerCounts.totalBoarded,
    crewMembers.length,
    infantPassengerEnabled,
    militaryPassengerEnabled,
    governmentPassengerEnabled,
    diplomatsPassengerEnabled,
  ]);

  // Convert empty strings to 0 for calculation purposes
  const getNumericValue = (value) => {
    return value === "" ? 0 : value || 0;
  };

  // Validation function to check if totalBoarded >= totalExempted (allows 0)
  const validatePassengerCounts = (totalBoarded, totalExempted) => {
    const boarded = getNumericValue(totalBoarded);
    const exempted = getNumericValue(totalExempted);
    if (boarded < exempted) {
      return false;
    } else {
      return true;
    }
  };

  const handleInputChange = (field, value) => {
    let numValue;

    if (value === null || value === undefined || value === "") {
      numValue = ""; // Allow empty string in UI
    } else {
      const parsed = parseInt(value);
      if (isNaN(parsed)) {
        numValue = ""; // Allow empty string in UI
      } else {
        // Prevent negative numbers by setting them to 0
        numValue = parsed < 0 ? 0 : parsed;
      }
    }
    setPassengerCounts((prev) => ({
      ...prev,
      [field]: numValue,
    }));
  };

  const handleSave = () => {
    // Convert empty strings to 0 before sending to API
    // Extract IDs from crew members for airlineDeadheadCrewIds
    // Use ONLY the actual ID from the API response - do NOT construct from name/seatNumber
    // The API expects only IDs, not name or seatNumber fields
    const airlineDeadheadCrewIds = crewMembers
      .map((member) => {
        // Use the actual ID from the API response (actualId, id, passengerId, or profileId)
        // Do NOT construct ID from passengerName-seatNumber - use only what API provides
        return (
          member.actualId ||
          member.id ||
          member.passengerId ||
          member.profileId ||
          null
        );
      })
      .filter((id) => id); // Filter out any null/undefined IDs

    const dataToSave = {
      totalBoarded:
        passengerCounts.totalBoarded === "" ? 0 : passengerCounts.totalBoarded,
      totalExempted:
        passengerCounts.totalExempted === ""
          ? 0
          : passengerCounts.totalExempted,
      infant: passengerCounts.infant === "" ? 0 : passengerCounts.infant,
      military: passengerCounts.military === "" ? 0 : passengerCounts.military,
      government:
        passengerCounts.government === "" ? 0 : passengerCounts.government,
      diplomat: passengerCounts.diplomat === "" ? 0 : passengerCounts.diplomat,
      airlineDeadheadCrewIds: airlineDeadheadCrewIds,
    };

    // Validate before saving
    const isValid = validatePassengerCounts(
      dataToSave.totalBoarded,
      dataToSave.totalExempted
    );
    if (isValid) {
      setError("");
      setErrorKey(0);
      onSave(dataToSave);
    } else {
      setError(
        "Total boarded passengers must be greater than or equal to total exempted passengers"
      );
      setErrorKey((prev) => prev + 1);
    }
  };

  const handleCrewMembersSave = (savedCrewMembers, count) => {
    // Save only the length of the count
    const membersToSave = savedCrewMembers.slice(
      0,
      count || savedCrewMembers.length
    );
    setCrewMembers(membersToSave);
    setIsCrewMembersDialogOpen(false);
  };

  const handleRemoveCrewMember = (memberId) => {
    setCrewMembers((prev) => {
      return prev.filter((member) => {
        const mId = member.id || member.profileId || member.actualId;
        return mId !== memberId;
      });
    });
  };

  if (!isOpen) return null;

  return (
    <div className="passenger-count-modal-overlay">
      <div className="passenger-count-modal-content">
        <div className="passenger-count-modal-header">
          <h2 className="passenger-count-modal-title">Passenger(s) Count</h2>
          <button className="passenger-count-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="passenger-count-form">
          <div className="passenger-count-input-group">
            <label htmlFor="totalBoarded">
              Total Boarded Passengers (including exemptions)
            </label>
            <input
              id="totalBoarded"
              type="number"
              value={passengerCounts.totalBoarded}
              onChange={(e) =>
                handleInputChange("totalBoarded", e.target.value)
              }
              className="passenger-count-input active"
            />
          </div>

          <div className="passenger-count-input-group">
            <label htmlFor="totalExempted">Total Exempted Passengers</label>
            <input
              id="totalExempted"
              type="number"
              value={passengerCounts.totalExempted}
              className="passenger-count-input disabled"
              disabled
            />
          </div>
          {infantPassengerEnabled && (
            <div className="passenger-count-input-group">
              <label htmlFor="infant">Infant</label>
              <input
                id="infant"
                type="number"
                value={passengerCounts.infant}
                onChange={(e) => handleInputChange("infant", e.target.value)}
                className="passenger-count-input"
              />
            </div>
          )}
          {militaryPassengerEnabled && (
            <div className="passenger-count-input-group">
              <label htmlFor="military">Military</label>
              <input
                id="military"
                type="number"
                value={passengerCounts.military}
                onChange={(e) => handleInputChange("military", e.target.value)}
                className="passenger-count-input"
              />
            </div>
          )}

          {governmentPassengerEnabled && (
            <div className="passenger-count-input-group">
              <label htmlFor="government">Government</label>
              <input
                id="government"
                type="number"
                value={passengerCounts.government}
                onChange={(e) =>
                  handleInputChange("government", e.target.value)
                }
                className="passenger-count-input"
              />
            </div>
          )}

          {diplomatsPassengerEnabled && (
            <div className="passenger-count-input-group">
              <label htmlFor="diplomat">Diplomat</label>
              <input
                id="diplomat"
                type="number"
                value={passengerCounts.diplomat}
                onChange={(e) => handleInputChange("diplomat", e.target.value)}
                className="passenger-count-input"
              />
            </div>
          )}

          <div className="passenger-count-input-group">
            <label>Crew Members</label>
            <Button
              variant="outlined"
              startIcon={<PlusOutlined />}
              onClick={() => setIsCrewMembersDialogOpen(true)}
              className="passenger-count-add-crew-button"
            >
              Add
            </Button>
            {crewMembers.length > 0 && (
              <div className="passenger-count-crew-members-list">
                {crewMembers.map((member, index) => {
                  const memberId = member.id || member.profileId || member.actualId;
                  const displayValue = member.seatNumber || member.age;
                  return (
                    <div
                      key={memberId || index}
                      className="passenger-count-crew-member-item"
                    >
                      <span className="passenger-count-crew-member-name">
                        {member.name} - {displayValue}
                      </span>
                      <button
                        className="passenger-count-crew-member-delete"
                        onClick={() => handleRemoveCrewMember(memberId)}
                        title="Remove crew member"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {error && (
          <SnackbarAlert key={errorKey} type="error">
            {error}
          </SnackbarAlert>
        )}

        <Button
          className="passenger-count-save-button"
          variant="contained"
          onClick={!isLoading ? handleSave : null}
          disabled={isLoading}
          endIcon={
            isLoading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          Save
        </Button>
      </div>

      <AddCrewMembersDialog
        isOpen={isCrewMembersDialogOpen}
        onClose={() => setIsCrewMembersDialogOpen(false)}
        onSave={handleCrewMembersSave}
        initialCrewMembers={crewMembers}
        initialCount={crewMembers.length}
        isLoading={false}
        flightPlanId={initialData?.id}
      />
    </div>
  );
};

export default AddPassengerCountDialog;
